import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ApiPreviewSection = () => {
  const [activeTab, setActiveTab] = useState('send-message');

  const codeExamples = {
    'send-message': {
      title: 'Send Message',
      language: 'javascript',
      code: `// Send a message to a channel
const response = await messagingHub.channels.send({
  channelId: 'general-chat',
  message: {
    text: 'Hello, team! 👋',
    type: 'text',
    metadata: {
      priority: 'normal',
      mentions: ['@john', '@sarah']
    }
  },
  sender: {
    id: 'user_123',
    name: 'Alex Johnson'
  }
});

console.log('Message sent:', response.messageId);`
    },
    'create-channel': {
      title: 'Create Channel',
      language: 'javascript',
      code: `// Create a new messaging channel
const channel = await messagingHub.channels.create({
  name: 'project-alpha',
  type: 'private',
  description: 'Discussion for Project Alpha',
  settings: {
    maxMembers: 50,
    messageRetention: '30d',
    allowFileUploads: true,
    moderationEnabled: true
  },
  members: [
    { id: 'user_123', role: 'admin' },
    { id: 'user_456', role: 'member' }
  ]
});

console.log('Channel created:', channel.id);`
    },
    'real-time': {
      title: 'Real-time Events',
      language: 'javascript',
      code: `// Subscribe to real-time events
const connection = messagingHub.connect({
  apiKey: 'your-api-key',
  userId: 'user_123'
});

// Listen for new messages
connection.on('message.received', (message) => {
  console.log('New message:', message);
  updateUI(message);
});

// Listen for user presence
connection.on('user.presence', (presence) => {
  console.log('User status:', presence);
  updateUserStatus(presence.userId, presence.status);
});

// Handle connection events
connection.on('connected', () => {
  console.log('Connected to MessagingHub');
});`
    },
    'push-notifications': {
      title: 'Push Notifications',
      language: 'javascript',
      code: `// Send push notifications
const notification = await messagingHub.notifications.send({
  recipients: ['user_123', 'user_456'],
  title: 'New Message in Project Alpha',
  body: 'Alex Johnson: Hey team, check out the latest updates!',
  data: {
    channelId: 'project-alpha',
    messageId: 'msg_789',
    action: 'open_channel'
  },
  platforms: {
    ios: {
      badge: 1,
      sound: 'default'
    },
    android: {
      icon: 'notification_icon',
      color: '#3B82F6'
    }
  }
});

console.log('Notification sent:', notification.id);`
    }
  };

  const tabs = [
    { id: 'send-message', label: 'Send Message', icon: 'Send' },
    { id: 'create-channel', label: 'Create Channel', icon: 'Plus' },
    { id: 'real-time', label: 'Real-time', icon: 'Zap' },
    { id: 'push-notifications', label: 'Notifications', icon: 'Bell' }
  ];

  const features = [
    {
      icon: 'Code',
      title: 'RESTful APIs',
      description: 'Clean, intuitive REST endpoints'
    },
    {
      icon: 'Zap',
      title: 'WebSocket Support',
      description: 'Real-time bidirectional communication'
    },
    {
      icon: 'Shield',
      title: 'Authentication',
      description: 'JWT tokens and API key security'
    },
    {
      icon: 'FileText',
      title: 'OpenAPI Spec',
      description: 'Complete API documentation'
    }
  ];

  const copyToClipboard = (text) => {
    navigator.clipboard?.writeText(text);
  };

  return (
    <section className="py-24 bg-card/30">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-secondary/10 border border-secondary/20 rounded-full px-4 py-2 mb-6">
            <Icon name="Code" size={16} className="text-secondary" />
            <span className="text-sm font-medium text-secondary">
              Developer Experience
            </span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Simple APIs that
            <span className="block text-secondary">just work</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Get started in minutes with our intuitive APIs. From simple messaging to complex 
            real-time applications, we've got you covered.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Code Examples */}
          <div className="order-2 lg:order-1">
            {/* Tab Navigation */}
            <div className="flex flex-wrap gap-2 mb-6">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`inline-flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    activeTab === tab?.id
                      ? 'bg-primary text-primary-foreground shadow-elevation-1'
                      : 'bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  <Icon name={tab?.icon} size={16} />
                  <span>{tab?.label}</span>
                </button>
              ))}
            </div>

            {/* Code Block */}
            <div className="relative bg-background border border-border rounded-xl overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-muted/30">
                <div className="flex items-center space-x-3">
                  <div className="flex space-x-2">
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                    <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  </div>
                  <span className="text-sm font-medium text-foreground">
                    {codeExamples?.[activeTab]?.title}
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Copy"
                  onClick={() => copyToClipboard(codeExamples?.[activeTab]?.code)}
                  className="text-muted-foreground hover:text-foreground"
                >
                  Copy
                </Button>
              </div>

              {/* Code Content */}
              <div className="p-6 overflow-x-auto">
                <pre className="text-sm text-foreground font-mono leading-relaxed">
                  <code>{codeExamples?.[activeTab]?.code}</code>
                </pre>
              </div>
            </div>

            {/* Quick Links */}
            <div className="flex flex-wrap gap-3 mt-6">
              <Button
                variant="outline"
                size="sm"
                iconName="ExternalLink"
                iconPosition="right"
                onClick={() => window.location.href = '/api-documentation'}
              >
                Full Documentation
              </Button>
              <Button
                variant="ghost"
                size="sm"
                iconName="Download"
                iconPosition="left"
              >
                Download SDK
              </Button>
              <Button
                variant="ghost"
                size="sm"
                iconName="Github"
                iconPosition="left"
              >
                View on GitHub
              </Button>
            </div>
          </div>

          {/* Features List */}
          <div className="order-1 lg:order-2">
            <div className="space-y-8">
              {features?.map((feature, index) => (
                <div key={index} className="flex items-start space-x-4">
                  <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg flex-shrink-0">
                    <Icon name={feature?.icon} size={24} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">
                      {feature?.title}
                    </h3>
                    <p className="text-muted-foreground">
                      {feature?.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Stats */}
            <div className="mt-12 grid grid-cols-2 gap-6">
              <div className="text-center p-6 bg-card border border-border rounded-xl">
                <div className="text-3xl font-bold text-primary mb-2">99.9%</div>
                <div className="text-sm text-muted-foreground">API Uptime</div>
              </div>
              <div className="text-center p-6 bg-card border border-border rounded-xl">
                <div className="text-3xl font-bold text-secondary mb-2">&lt;50ms</div>
                <div className="text-sm text-muted-foreground">Response Time</div>
              </div>
            </div>

            {/* CTA */}
            <div className="mt-8">
              <Button
                variant="default"
                size="lg"
                iconName="ArrowRight"
                iconPosition="right"
                fullWidth
                onClick={() => window.location.href = '/developer-portal'}
                className="shadow-elevation-2"
              >
                Start Building Now
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ApiPreviewSection;